import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScanLine } from "lucide-react";

// Camera barcode/QR scanner (mobile-first). Calls onScan(code) for each decoded code; stays open for continuous scanning.
export function BarcodeScannerDialog({ open, onClose, onScan, lastHit }) {
  const ref = useRef(null);
  const lastRef = useRef({ code: "", at: 0 });
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!open) return;
    let scanner;
    const start = async () => {
      try {
        scanner = new Html5Qrcode("td-scanner", { verbose: false });
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 260, height: 160 } },
          (text) => {
            const now = Date.now();
            if (text === lastRef.current.code && now - lastRef.current.at < 2500) return;
            lastRef.current = { code: text, at: now };
            if (navigator.vibrate) navigator.vibrate(60);
            onScan(text);
          }, () => {});
        ref.current = scanner;
      } catch (e) { setError("Camera not available. Allow camera access or type the barcode below."); }
    };
    start();
    return () => { const s = ref.current; ref.current = null; if (s) s.stop().then(() => s.clear()).catch(() => {}); };
  }, [open, onScan]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="w-5 h-5 text-[#0B7C5A]" /> Scan product barcode</DialogTitle></DialogHeader>
        <div id="td-scanner" className="w-full rounded-xl overflow-hidden bg-black min-h-[220px]" data-testid="barcode-scanner-view" />
        {error && <div className="text-xs text-[#B45309] bg-[#FEF3C7]/60 rounded-lg px-3 py-2" data-testid="barcode-scanner-error">{error}</div>}
        {lastHit && <div className="text-sm rounded-lg px-3 py-2 bg-[#0B7C5A]/10 text-[#0B7C5A] font-medium" data-testid="barcode-last-hit">{lastHit}</div>}
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (manual.trim()) { onScan(manual.trim()); setManual(""); } }}>
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Or type barcode / product code" className="h-10" data-testid="barcode-manual-input" />
          <Button type="submit" className="h-10 bg-[#101D51]" data-testid="barcode-manual-add">Add</Button>
        </form>
        <p className="text-[11px] text-[#6B7189]">Keep scanning to add more items; scanning the same code again increases its quantity.</p>
      </DialogContent>
    </Dialog>
  );
}
